import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpider, faPlay, faStop, faSpinner, faSync } from '@fortawesome/free-solid-svg-icons';
import spiderService from '../../services/spiderServiceFrontend';
import authProfileService from '../../services/authProfileService';

const SpiderPanel = ({ webTarget, isDarkMode, onRefresh }) => {
  const [status, setStatus] = useState({ active: false });
  const [authProfiles, setAuthProfiles] = useState([]);
  const [config, setConfig] = useState({
    maxDepth: 5,
    maxRequests: 500,
    authProfileId: '',
    respectRobotsTxt: true
  });
  const [polling, setPolling] = useState(null);

  useEffect(() => {
    if (webTarget?.project) {
      authProfileService.getAll(webTarget.project).then(setAuthProfiles).catch(() => {});
    }
    checkStatus();

    return () => { if (polling) clearInterval(polling); };
  }, [webTarget]);

  const checkStatus = async () => {
    try {
      const s = await spiderService.getStatus(webTarget._id);
      setStatus(s);
    } catch {}
  };

  const handleStart = async () => {
    try {
      await spiderService.start({
        webTargetId: webTarget._id,
        projectId: webTarget.project,
        ...config
      });
      setStatus({ active: true, stats: { pagesDiscovered: 0, requestsSent: 0, errors: 0, duplicatesSkipped: 0 } });

      const interval = setInterval(async () => {
        const s = await spiderService.getStatus(webTarget._id);
        setStatus(s);
        if (!s.active) {
          clearInterval(interval);
          setPolling(null);
          onRefresh?.();
        }
      }, 2000);
      setPolling(interval);
    } catch (err) {
      console.error('Spider start error:', err);
    }
  };

  const handleStop = async () => {
    try {
      await spiderService.stop(webTarget._id);
      if (polling) { clearInterval(polling); setPolling(null); }
      checkStatus();
      onRefresh?.();
    } catch {}
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-3">
        <FontAwesomeIcon icon={faSpider} className="text-purple-400 text-lg" />
        <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Spider / Crawler</h3>
        {status.active && <FontAwesomeIcon icon={faSpinner} spin className="text-blue-400" />}
      </div>

      {/* Config */}
      <div className={`grid grid-cols-2 gap-4 p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div>
          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max Depth</label>
          <input type="number" value={config.maxDepth} onChange={e => setConfig(p => ({...p, maxDepth: parseInt(e.target.value)}))}
            className={`w-full text-xs rounded border px-2 py-1.5 mt-1 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700'}`} />
        </div>
        <div>
          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Max Requests</label>
          <input type="number" value={config.maxRequests} onChange={e => setConfig(p => ({...p, maxRequests: parseInt(e.target.value)}))}
            className={`w-full text-xs rounded border px-2 py-1.5 mt-1 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700'}`} />
        </div>
        <div>
          <label className={`text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Auth Profile</label>
          <select value={config.authProfileId} onChange={e => setConfig(p => ({...p, authProfileId: e.target.value}))}
            className={`w-full text-xs rounded border px-2 py-1.5 mt-1 ${isDarkMode ? 'bg-gray-900 border-gray-600 text-gray-300' : 'bg-gray-50 border-gray-300 text-gray-700'}`}>
            <option value="">None (unauthenticated)</option>
            {authProfiles.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={config.respectRobotsTxt}
              onChange={e => setConfig(p => ({...p, respectRobotsTxt: e.target.checked}))}
              className="rounded" />
            <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Respect robots.txt</span>
          </label>
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        {!status.active ? (
          <button onClick={handleStart}
            className="px-4 py-2 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faPlay} /> Start Crawl
          </button>
        ) : (
          <button onClick={handleStop}
            className="px-4 py-2 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700 flex items-center gap-1.5">
            <FontAwesomeIcon icon={faStop} /> Stop
          </button>
        )}
        <button onClick={() => { checkStatus(); onRefresh?.(); }}
          className={`px-4 py-2 rounded text-xs ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
          <FontAwesomeIcon icon={faSync} className="mr-1" /> Refresh
        </button>
      </div>

      {/* Stats */}
      {status.stats && (
        <div className={`grid grid-cols-4 gap-3 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          {[
            { label: 'Pages Discovered', value: status.stats.pagesDiscovered },
            { label: 'Requests Sent', value: status.stats.requestsSent },
            { label: 'Duplicates Skipped', value: status.stats.duplicatesSkipped },
            { label: 'Errors', value: status.stats.errors }
          ].map(stat => (
            <div key={stat.label} className="text-center">
              <div className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{stat.value}</div>
              <div className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{stat.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Error display */}
      {status.lastError && (
        <div className={`p-2 rounded text-xs border ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
          Last error: {status.lastError}
        </div>
      )}
    </div>
  );
};

export default SpiderPanel;
